import { Component, ComponentInterface, Element, Event, EventEmitter, h, Method, Prop } from '@stencil/core';
import lottie from 'lottie-web/build/player/lottie_svg';
import { PlayerState, PlayMode } from '../../utils/animation';
import { parseSrc } from '../../utils/helpers';

@Component({
  tag: 'vds-animated-icon',
  styleUrl: 'vds-animated-icon.scss',
  shadow: true,
})
export class VdsAnimatedIcon implements ComponentInterface {

  container!: HTMLElement;


  @Element() el: HTMLElement;

  /**
   * Play mode.
   */
  @Prop() mode: PlayMode = PlayMode.Normal;
  /**
   * Autoplay animation on load
   */
  @Prop() autoplay = false;
  /**
   * Background color.
   */
  @Prop({ reflect: true }) background? = 'transparent';

  /**
   * Number of times to loop animation.
   */
  @Prop() count?: number;
  /**
   * Direction of animation
   */
  @Prop() direction = 1;
  /**
   * Whether to play on mouse hover
   */
  @Prop() hover = false;
  /**
   * Whether to loop animation
   */
  @Prop({ reflect: true }) loop = false;
  /**
   * Renderer to use.
   */
  @Prop() renderer: 'svg' = 'svg';
  /**
   * Animation speed.
   */
  @Prop() speed = 1;
  /**
   * Bodymovin JSON data or URL to json.
   */
  @Prop() src?: string;

  /**
   * Player state.
   */
  @Prop({ mutable: true }) currentState: PlayerState = PlayerState.Loading;

  @Prop({ mutable: true }) seeker: any;

  @Prop() intermission = 1;

  @Prop() rewind: boolean = false;

  @Prop() initialSegment: number = 0;

  @Prop() playSegments?: [number, number];

  @Prop() playSegmentStart?: number;

  @Prop() playSegmentEnd?: number;

  @Prop() rewindSegments?: [number, number];

  @Prop() rewindSegmentStart?: number;

  @Prop() rewindSegmentEnd?: number;

  @Prop() clickSegments?: [number, number];

  @Prop() clickSegmentStart?: number;

  @Prop() clickSegmentEnd?: number;

  @Prop() interactionElement?: HTMLElement;

  @Prop() interactionElementRef?: string;

  @Prop() width: string = 'auto';

  @Prop() height: string = 'auto';

  @Prop() transform: string = 'translate3d(0px, 0px, 0px)';

  @Prop() centered: boolean = false;

  @Prop() verticalCentered: boolean = false;

  @Event() containsError: EventEmitter;

  @Event() frame: EventEmitter;

  @Event() hasCompleted: EventEmitter;

  @Event() looped: EventEmitter;

  @Event() ready: EventEmitter;

  @Event() loaded: EventEmitter;

  @Event() isPlaying: EventEmitter;

  @Event() paused: EventEmitter;

  @Event() stopped: EventEmitter;

  @Event() freezed: EventEmitter;

  private _io?: any;
  private _lottie?: any;
  private _counter = 0;
  private _rewindOnComplete: boolean = false;

  constructor() {
    if (!isNaN(this.playSegmentStart) && !isNaN(this.playSegmentEnd)) {
      this.playSegments = [this.playSegmentStart, this.playSegmentEnd];
    }

    if (!isNaN(this.rewindSegmentStart) && !isNaN(this.rewindSegmentEnd)) {
      this.rewindSegments = [this.rewindSegmentStart, this.rewindSegmentEnd];
    }

    if (!isNaN(this.clickSegmentStart) && !isNaN(this.clickSegmentEnd)) {
      this.clickSegments = [this.clickSegmentStart, this.clickSegmentEnd];
    }

    if (this.interactionElementRef) {
      this.interactionElement = document.querySelector(this.interactionElementRef);
    }
  }

  componentDidLoad(): void {
    // Add intersection observer for detecting component being out-of-view.
    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) {
          if ((this.currentState === (PlayerState.Loading || PlayerState.Frozen)) && this.autoplay) {
            setTimeout(() => this.play(this.playSegments), this.intermission);
          }
        } else if (this.currentState === PlayerState.Playing) {
          this.freeze();
        }
      });

      this._io.observe(this.container);
    }

    // Add listener for Visibility API's change event.
    if (typeof document.hidden !== 'undefined') {
      document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }

    // Setup lottie player
    if (this.src) {
      this.load(this.src);
    }
  }

  load(src: string | object) {
    const options: any = {
      container: this.container,
      loop: false,
      autoplay: false,
      renderer: this.renderer,
      rendererSettings: {
        scaleMode: 'noScale',
        clearCanvas: false,
        progressiveLoad: true,
        hideOnTransparent: true,
      },
    };

    try {
      const srcParsed = parseSrc(src);
      const srcAttrib = typeof srcParsed === 'string' ? 'path' : 'animationData';

      // Clear previous animation, if any
      if (this._lottie) {
        this._lottie.destroy();
      }

      // Initialize lottie player and load animation
      this._lottie = lottie.loadAnimation({
        ...options,
        [srcAttrib]: srcParsed,
      });

      if (this.initialSegment) {
        this._lottie.goToAndStop(this.initialSegment, true);
      }
    } catch (err) {
      this.currentState = PlayerState.Error;
      this.containsError.emit();
      return;
    }

    if (this._lottie) {
      // Calculate and save the current progress of the animation
      this._lottie.addEventListener('enterFrame', () => {
        this.seeker = (this._lottie.currentFrame / this._lottie.totalFrames) * 100;

        this.frame.emit({
          frame: this._lottie.currentFrame,
          seeker: this.seeker,
        });
      });

      // Handle animation play complete
      this._lottie.addEventListener('complete', () => {
        if (this.currentState !== PlayerState.Playing) {
          this.hasCompleted.emit();
          return;
        }

        if ((!this.loop && !this.rewind) || (this.count && this._counter >= this.count)) {
          this.hasCompleted.emit();
          return;
        }

        if (this.rewind) {
          this.hasCompleted.emit();
          this.pause();
          if (this._rewindOnComplete) {
            this.handleMouseLeave();
          }
          else {
            this._lottie.setDirection(1);
          }
          return;
        }

        if (this.mode === PlayMode.Bounce) {
          if (this.count) {
            this._counter += 0.5;
          }

          setTimeout(() => {
            this.looped.emit();

            if (this.currentState === PlayerState.Playing) {
              this._lottie.play();
            }
          }, this.intermission);
        } else {
          if (this.count) {
            this._counter += 1;
          }

          window.setTimeout(() => {
            this.looped.emit();

            if (this.currentState === PlayerState.Playing) {
              this._lottie.stop();
              this._lottie.play();
            }
          }, this.intermission);
        }
      });

      // Handle lottie-web ready event
      this._lottie.addEventListener('DOMLoaded', () => {
        const svg = this.container.querySelector('svg');
        svg.style.transform = this.transform;
        svg.style.width = this.width;
        svg.style.height = this.height;
        this.ready.emit();
      });

      // Handle animation data load complete
      this._lottie.addEventListener('data_ready', () => {
        this.loaded.emit();
      });

      // Set error state when animation load fail event triggers
      this._lottie.addEventListener('data_failed', () => {
        this.currentState = PlayerState.Error;
        this.containsError.emit();
      });

      // Set handlers to auto play animation on hover if enabled
      this.el.addEventListener('mouseenter', () => {
        this.handleMouseEnter();
      });

      this.el.addEventListener('mouseleave', () => {
        this.handleMouseLeave();
      });

      this.el.addEventListener('click', () => {
        this.handleClick();
      });

      this.interactionElement?.addEventListener('mouseenter', () => {
        this.handleMouseEnter();
      });

      this.interactionElement?.addEventListener('mouseleave', () => {
        this.handleMouseLeave();
      });

      this.interactionElement?.addEventListener('click', () => {
        this.handleClick();
      });

      // Set initial playback speed and direction
      this.setSpeed(this.speed);
      this.setDirection(this.direction);
    }
  }

  @Method()
  async handleMouseEnter() {
    if (this.hover && this.currentState !== PlayerState.Playing) {
      this.play(this.playSegments);
    }
  }

  @Method()
  async handleMouseLeave() {
    if (this.hover) {
      if (this.rewind) {
        if (this.currentState !== PlayerState.Playing) {
          this._rewindOnComplete = false;
          setTimeout(() => {
            this.looped.emit();
            this._lottie.setDirection(-1);
            this.play(this.rewindSegments);
          }, this.intermission);
        }
        else {
          this._rewindOnComplete = true;
        }
      } else {
        if (this.currentState === PlayerState.Playing) {
          this.stop();
        }
      }
    }
  }

  @Method()
  async handleClick() {
    if (this.clickSegments) {
      this.play(this.clickSegments);
    }
  }

  /**
   * Start playing animation.
   */
  @Method()
  async play(segments?: [number, number]) {
    if (!this._lottie) {
      return;
    }

    if (segments) {
      this._lottie.playSegments(segments, true);
    }
    else {
      this._lottie.play();
    }

    this.currentState = PlayerState.Playing;
    this.isPlaying.emit();
  }

  /**
   * Stop playing animation.
   */
  @Method()
  async pause() {
    if (!this._lottie) {
      return;
    }
    this._lottie.pause();
    this.currentState = PlayerState.Paused;
    this.paused.emit();
  }

  /**
   * Stops animation play.
   */
  @Method()
  async stop() {
    if (!this._lottie) {
      return;
    }
    this._counter = 0;
    this._lottie.stop();
    this.currentState = PlayerState.Stopped;
    this.stopped.emit();
  }

  /**
   * Seek to a given frame.
   */
  @Method()
  async seek(value: number | string) {
    if (!this._lottie) {
      return;
    }

    // Extract frame number from either number or percentage value
    const matches = value.toString().match(/^([0-9]+)(%?)$/);
    if (!matches) {
      return;
    }

    // Calculate and set the frame number
    const frame = matches[2] === '%' ? (this._lottie.totalFrames * Number(matches[1])) / 100 : matches[1];

    // Set seeker to new frame number
    this.seeker = frame;

    // Send lottie player to the new frame
    if (this.currentState === PlayerState.Playing) {
      this._lottie.goToAndPlay(frame, true);
    } else {
      this._lottie.goToAndStop(frame, true);
      this._lottie.pause();
    }
  }

  /**
   * Freeze animation play.
   * This internal state pauses animation and is used to differentiate between
   * user requested pauses and component instigated pauses.
   */
  freeze() {
    if (!this._lottie) {
      return;
    }
    this._lottie.pause;
    this.currentState = PlayerState.Frozen;
    this.freezed.emit();
  }

  @Method()
  async getLottie() {
    return this._lottie;
  }

  /**
   * Sets animation play speed
   * @param value Playback speed.
   */
  @Method()
  async setSpeed(value = 1) {
    if (!this._lottie) {
      return;
    }
    this._lottie.setSpeed(value);
  }

  /**
   * Animation play direction.
   * @param value Direction values.
   */
  @Method()
  async setDirection(value: number) {
    if (!this._lottie) {
      return;
    }
    this._lottie.setDirection(value);
  }

  /**
   * Sets the looping of the animation.
   *
   * @param value Whether to enable looping. Boolean true enables looping.
   */
  @Method()
  async setLooping(value: boolean) {
    if (this._lottie) {
      this.loop = value;
      this._lottie.loop = value;
    }
  }

  /**
   * Toggle playing state.
   */
  @Method()
  async togglePlay() {
    return this.currentState === PlayerState.Playing ? this.pause() : this.play(this.playSegments);
  }

  /**
   * Toggles animation looping.
   */
  @Method()
  async toggleLooping() {
    this.setLooping(!this.loop);
  }

  render() {
    return (
      <div
        class="relative inline-grid box-border auto-cols-auto grid-rows-[auto] w-[inherit] h-[inherit]"
      >
        <div
          class={{
            "overflow-visible w-[inherit] h-[inherit]": true,
            "flex justify-center": this.centered,
            "flex items-center": this.verticalCentered
          }}
          style={{
            background: this.background,
          }}
          ref={ref => (this.container = ref)}
        >
          {this.currentState === PlayerState.Error ? <div class="flex h-full justify-center items-center">⚠️</div> : undefined}
        </div>
      </div>
    );
  }

  /**
   * Handle visibility change events.
   */
  private onVisibilityChange() {
    if (document.hidden === true && this.currentState === PlayerState.Playing) {
      this.freeze();
    } else if (this.currentState === PlayerState.Frozen) {
      this.play(this.playSegments);
    }
  }
}
